import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, ShieldCheck, IndianRupee } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { DONATION_IMPACTS, IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMOUNTS = [500, 1000, 2500, 5000, 10000];

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-hecso-ink focus:outline-none focus:ring-2 focus:ring-hecso-green focus:border-hecso-green transition";

export default function Donate() {
  const [frequency, setFrequency] = useState("one-time");
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [cause, setCause] = useState("Where it is needed most");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const finalAmount = custom ? Number(custom) : amount;
  const impact = DONATION_IMPACTS.find((d) => d.amount === finalAmount);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please choose or enter a valid donation amount.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/donations`, { ...form, amount: finalAmount, frequency, cause });
      toast.success(`Thank you, ${form.name.split(" ")[0]}! Your pledge of ₹${finalAmount.toLocaleString("en-IN")} has been recorded. Our team will contact you to complete the donation.`);
      setForm({ name: "", email: "", phone: "", message: "" });
      setCustom("");
      setAmount(1000);
    } catch {
      toast.error("Could not record your pledge. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="donate-page">
      <PageHeader
        title="Donate"
        subtitle="Your contribution directly funds health camps, eye screenings, education and empowerment programs across Assam."
      />

      <section className="py-16 sm:py-20 bg-hecso-beige">
        <div className="container-hecso grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <Reveal>
              <img src={IMAGES.childrenSchool} alt="Children supported by HECSO programs" className="rounded-2xl w-full h-64 object-cover shadow-md" />
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading text-xl font-semibold text-hecso-ink">What Your Donation Does</h2>
              <ul className="mt-4 space-y-3">
                {DONATION_IMPACTS.map((d) => (
                  <li key={d.amount} className="flex gap-3 rounded-lg bg-white border border-slate-200 px-4 py-3 text-sm text-hecso-muted" data-testid={`impact-amount-${d.amount}`}>
                    <span className="flex items-center gap-1 font-heading font-bold text-hecso-green whitespace-nowrap">
                      <IndianRupee className="w-4 h-4" />{d.amount.toLocaleString("en-IN")}
                    </span>
                    <span>{d.text}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={180}>
              <div className="flex gap-3 rounded-xl border border-emerald-200 bg-hecso-greensubtle p-5">
                <ShieldCheck className="w-8 h-8 shrink-0 text-hecso-green" />
                <p className="text-sm text-hecso-muted leading-relaxed">
                  Donations to HECSO are eligible for tax deduction under <strong className="text-hecso-ink">Section 80G</strong> of
                  the Income Tax Act, 1961. A receipt and 80G certificate will be issued for every contribution.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={150} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6" data-testid="donate-form">
              <div>
                <label className="block text-sm font-medium text-hecso-ink mb-2">Donation Frequency</label>
                <div className="grid grid-cols-2 gap-3">
                  {["one-time", "monthly"].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFrequency(f)}
                      data-testid={`frequency-${f}-btn`}
                      className={`rounded-lg border-2 px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${
                        frequency === f ? "border-hecso-green bg-hecso-greensubtle text-hecso-greendark" : "border-slate-200 text-hecso-muted hover:border-hecso-green"
                      }`}
                    >
                      {f === "one-time" ? "One-Time" : "Monthly"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-hecso-ink mb-2">Choose an Amount (₹)</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {AMOUNTS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => { setAmount(a); setCustom(""); }}
                      data-testid={`amount-${a}-btn`}
                      className={`rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                        !custom && amount === a ? "border-hecso-orange bg-hecso-orangelight text-amber-800" : "border-slate-200 text-hecso-muted hover:border-hecso-orange"
                      }`}
                    >
                      ₹{a.toLocaleString("en-IN")}
                    </button>
                  ))}
                </div>
                <div className="relative mt-3">
                  <IndianRupee className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-hecso-muted" />
                  <input
                    type="number"
                    min="1"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Enter a custom amount"
                    className={`${inputCls} pl-9`}
                    data-testid="custom-amount-input"
                  />
                </div>
                {impact && (
                  <p className="mt-3 rounded-lg bg-hecso-greensubtle px-4 py-2.5 text-sm text-hecso-greendark" data-testid="donation-impact-note">
                    ₹{finalAmount.toLocaleString("en-IN")} {impact.text}.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-hecso-ink mb-2">Support a Cause</label>
                <select value={cause} onChange={(e) => setCause(e.target.value)} className={inputCls} data-testid="donate-cause-select">
                  <option>Where it is needed most</option>
                  <option>Free Health Check-up Camps</option>
                  <option>Eye Screening & Cataract Detection</option>
                  <option>School Health Programs</option>
                  <option>Women Empowerment</option>
                  <option>Disability Skill Development</option>
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Full Name *</label>
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="donor-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Email *</label>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="donor-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Phone</label>
                  <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 XXXXX XXXXX" data-testid="donor-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Message (optional)</label>
                  <input value={form.message} onChange={set("message")} className={inputCls} placeholder="A note with your donation" data-testid="donor-message-input" />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="donate-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-hecso-orange px-8 py-3.5 text-sm font-semibold text-white transition-all hover:bg-hecso-orangehover hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
              >
                <HeartHandshake className="w-4 h-4" />
                {submitting ? "Processing..." : `Donate ₹${(finalAmount || 0).toLocaleString("en-IN")} ${frequency === "monthly" ? "Monthly" : "Now"}`}
              </button>
              <p className="text-center text-xs text-hecso-muted">
                This records your donation pledge — our team will contact you with secure payment options and your 80G receipt.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
